<template>
  <div class="tag-editor-container">
    <div class="title">
      <div class="tag-name">{{ title }}</div>
      <div v-if="enableEdit" class="btn-container">
        <a
          v-if="!hiddenAddBtn"
          class="add-tag-btn icomoon-add-box"
          href="/v-admin/crm-management/tag/add"
          target="_blank"
        >
        </a>
      </div>
    </div>
    <div class="tag-editor">
      <transition name="slide">
        <div class="selector-container">
          <TagSelect
            v-if="isSmart"
            v-model="selected"
            class="tag-selector"
            multiple
            :totalTagList="tagList"
            :tagTypes="tagTypes"
            :totalTagLoader="tagListLoader"
            :isDisabled="isDisabled"
            @save="handleAddTagBtnClick"
          >
          </TagSelect>
          <custom-remote-el-select
            v-else
            v-model="selected"
            filterable
            multiple
            remote
            popper-class="tag-selector"
            value-key="id"
            :disabled="isDisabled"
            :loading="tagListLoading"
            @visible-change="onVisibleChange"
          >
            <template v-for="tag in selectableTagList">
              <el-option
                v-if="!tag.isSelected"
                :key="tag.id"
                :label="tag.i18nName"
                :value="tag"
              >
              </el-option>
            </template>
          </custom-remote-el-select>
          <div class="btn-container">
            <el-button v-if="hiddenAddTagBtn" class="add-tag-btn" :disabled="isDisabled" @click="handleAddTagBtnClick">{{ $t('common.btn.add') }}</el-button>
          </div>
        </div>
      </transition>
      <div class="selected-container">
        <div class="tag">
          <div class="others-tags" :style="tagContainerStyle">
            <div class="tag-wrapper" ref="tagContainer">
              <el-tooltip
                v-for="(tag, tagIndex) in othersTagType"
                v-show="tag.isSelected"
                effect="light"
                placement="bottom"
                popper-class="light-tooltip-popper"
                :key="tagIndex"
              >
                <el-tag
                  type="info"
                  class="tag-item"
                  color="#EAEAEA"
                  :class="isDisabled && 'is-disabled'"
                >
                  <span>{{ tag.i18nName }}</span>
                  <el-popconfirm
                    :title="$t(`${i18nPrefix}.message.confirmDeleteTagTip`)"
                    @confirm="confirmDeleteTag"
                    @cancel="cancelDeleteTag"
                  >
                    <template v-if="!isDisabled" #reference>
                      <i v-if="tag.category !== 'campaign'" class="el-tag__close el-icon-close" @click="handleTagDeleteBtnClick(tag)"></i>
                    </template>
                  </el-popconfirm>
                </el-tag>
                <template #content>
                  <div class="tag">{{ tag.i18nName }}</div>
                  <div v-if="tag.i18nContent" class="content">{{ tag.i18nContent }}</div>
                  <div class="operator">{{ getOperatorName(tag) }}</div>
                  <div class="operate-time">{{ tag.operate_time|formatDate }}</div>
                </template>
              </el-tooltip>
            </div>
          </div>
          <div v-show="showExpandBtn" class="show-all" :class="isExpand && 'show'">
            <i @click="handleExpandBtnClick" class="expand-icon iconfont icondown"></i>
          </div>
          <div class="tag-wrapper">
            <el-collapse v-if="tagTypesList.length" v-model="tagCollapseActiveList" class="tag-collapse">
              <el-collapse-item
                v-for="(item, index) in tagTypesList"
                class="tag-collapse-item"
                :key="index"
                :title="item.label"
                :name="item.type"
              >
                <el-tooltip
                  v-for="(tag, tagIndex) in item.tagList"
                  v-show="tag.isSelected"
                  effect="light"
                  placement="bottom"
                  popper-class="light-tooltip-popper"
                  :key="tagIndex"
                >
                  <el-tag
                    type="info"
                    class="tag-item"
                    color="#EAEAEA"
                    :class="isDisabled && 'is-disabled'"
                  >
                    {{ tag.i18nName }}
                    <el-popconfirm
                      :title="$t(`${i18nPrefix}.message.confirmDeleteTagTip`)"
                      @confirm="confirmDeleteTag"
                      @cancel="cancelDeleteTag"
                    >
                      <template v-if="!isDisabled" #reference>
                        <i v-if="tag.category !== 'campaign'" class="el-tag__close el-icon-close" @click="handleTagDeleteBtnClick(tag)"></i>
                      </template>
                    </el-popconfirm>
                  </el-tag>
                  <template #content>
                    <div class="tag">{{ tag.i18nName }}</div>
                    <div v-if="tag.i18nContent" class="content">{{ tag.i18nContent }}</div>
                    <div class="operator">{{ getOperatorName(tag) }}</div>
                    <div class="operate-time">{{ tag.operate_time|formatDate }}</div>
                  </template>
                </el-tooltip>
              </el-collapse-item>
            </el-collapse>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import moment from 'moment'
import TagSelect from './tagSelect.vue'

export default {
  components: {
    TagSelect
  },
  props: {
    tagList: {
      type: Array,
      required: true
    },
    tagListLoader: {
      default: null
    },
    tagTypes: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    hiddenAddBtn: {
      type: Boolean,
      default: true
    },
    hiddenAddTagBtn: {
      type: Boolean,
      default: false
    },
    isSmart: {
      type: Boolean,
      default: false
    },
    enableEdit: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      selected: null,
      isExpand: false,
      tagContainerStyle: null,
      limitedHeight: null,
      tagContainerHeight: null,
      tagCollapseActiveList: [],
      tagListLoading: false,
      tagListLoaded: false,
      cascaderProps: {
        multiple: true,
        value: 'id',
        label: 'name'
      },
      deleteTag: null
    }
  },
  computed: {
    selectableTagList () {
      return _.filter(this.tagList, tag => tag.type !== 'system' && !tag.isSelected)
    },
    showExpandBtn () {
      return this.tagContainerHeight > this.limitedHeight
    },
    othersTagType () {
      return _.chain(this.tagList).filter(tag => tag.isSelected).filter(tag => tag.type === 'OTHERS').value()
    },
    tagTypesList () {
      const vm = this
      return _.chain(this.tagList).filter(tag => tag.isSelected).filter(tag => tag.type !== 'OTHERS').groupBy(tag => tag.type).map((val, key) => {
        return {
          get label () {
            return vm.$t(`admin.crm.tagTypes.${key}`)
          },
          type: key,
          order: _.indexOf(this.tagTypes, key),
          tagList: val
        }
      }).orderBy('order').value()
    }
  },
  created () {
  },
  mounted () {
    const tagEl = document.createElement('span')
    tagEl.className = 'tag-item el-tag'
    tagEl.style.marginTop = '14px'
    tagEl.style.visibility = 'hidden'
    this.$refs.tagContainer.appendChild(tagEl)
    const style = window.getComputedStyle(tagEl)
    this.limitedHeight = (tagEl.offsetHeight + parseInt(style.marginTop)) * 4
    this.tagContainerStyle = {
      maxHeight: `${this.limitedHeight}px`,
      overflow: 'hidden'
    }
    tagEl.remove()
    this.getTagContainerHeight()
  },
  methods: {
    ...mapGetters('account', ['getCurrentAccount']),
    handleTagDeleteBtnClick (tag) {
      this.deleteTag = tag
    },
    confirmDeleteTag () {
      this.$emit('delete', this.deleteTag.id, this.isSmart)
    },
    cancelDeleteTag () {
      this.deleteTag = null
    },
    async onVisibleChange (visible) {
      if (!visible || !this.tagListLoader || this.tagListLoaded) {
        this.tagListLoading = false
        return
      }
      this.tagListLoading = true
      try {
        await this.tagListLoader()
        this.tagListLoaded = true
      } finally {
        this.tagListLoading = false
      }
    },
    getOperatorName (tag) {
      const operator = tag.operator
      if (operator && operator.id) {
        return tag.operator.name
      } else {
        return this.$t('admin.crm.label.tag.systemOperator')
      }
    },
    handleAddTagBtnClick () {
      if (!this.selected || this.selected.length < 1) {
        return
      }
      const account = this.getCurrentAccount()
      const today = moment().format('YYYY-MM-DD')
      if (this.isSmart) {
        const selectedTagList = _.filter(this.tagList, tag => this.selected.includes(tag.id))
        const tagCollapseActiveList = []
        _.forEach(selectedTagList, tag => {
          if (tag.category) {
            tag.isSelected = true
            tag.operator.id = account.id
            tag.operator.name = account.full_name
            tag.operate_time = today
            _.remove(this.tagList, { id: tag.id })
            this.tagList.splice(0, 0, tag)
            tagCollapseActiveList.push(tag.type)
          }
        })
        if (!_.isEmpty(tagCollapseActiveList)) {
          this.tagCollapseActiveList = tagCollapseActiveList
        }
      } else {
        _.forEach(this.selected, item => {
          item.isSelected = true
          item.operator.id = account.id
          item.operator.name = account.full_name
          item.operate_time = today
          _.remove(this.tagList, { id: item.id })
          this.tagList.splice(0, 0, item)
        })
      }
      this.selected = null
      this.$emit('save', this.isSmart)
    },
    handleExpandBtnClick () {
      this.isExpand = !this.isExpand
      this.computedTagContainerHeight()
    },
    computedTagContainerHeight () {
      if (this.isExpand) {
        this.tagContainerStyle = {
          maxHeight: `${this.tagContainerHeight}px`
        }
      } else {
        this.tagContainerStyle = {
          maxHeight: `${this.limitedHeight}px`,
          overflow: 'hidden'
        }
      }
    },
    getTagContainerHeight () {
      this.$nextTick(() => {
        this.tagContainerHeight = this.$refs.tagContainer.offsetHeight
        this.computedTagContainerHeight()
      })
    }
  },
  watch: {
    othersTagType: {
      handler () {
        this.getTagContainerHeight()
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.tag-editor-container {
  .title {
    display: flex;
    align-items: center;
    padding: 0 16px;
    height: 46px;
    background-color: #F6F6F6;

    .tag-name {
      font-size: 16px;
      font-weight: bold;
    }

    .btn-container {
      display: flex;
      align-items: center;
      margin-left: auto;
      height: 46px;
      line-height: 46px;

      .add-tag-btn {
        &::before {
          font-size: 20px;
          color: #0096CC;
        }
      }

      .save-btn {
        padding-left: 10px;
        color: #0096CC;
        font-size: 20px;
      }

      .editor-btn {
        color: #0096CC;
        font-size: 18px;
      }
    }
  }

  .tag-editor {
    margin-top: 20px;

    .selector-container {
      display: flex;

      .tag-selector {
        width: 100%;
        line-height: 32px;

        ::v-deep {
          .el-input__inner {
            height: 32px;
            line-height: 32px;
          }
        }
      }

      &.slide-enter-active {
        opacity: 0;
        max-height: 0;
        transition: all .3s ease;
      }

      &.slide-leave-active {
        max-height: 32px;
        opacity: 0;
        transition: all .3s ease-out;
      }

      &.slide-enter-to {
        opacity: 1;
        max-height: 32px;
      }

      &.slide-leave-to {
        max-height: 0;
        opacity: 0;
      }

      .add-tag-btn {
        padding: 8px 25px;
        margin-left: 20px;
        background-color: #0096cc;
        color: #fff;

        &:hover {
          background-color: #0089B2;
        }

        &.is-disabled {
          background-color: #fff;
          border-color: #ebeef5;
          color: #c0c4cc;

          &:hover {
            background-color: #fff;
          }
        }
      }
    }

    .selected-container {
      .tag {
        transition: .3s all ease-out;
        .others-tags {
          margin-bottom: 10px;
        }
        .tag-wrapper {
          .tag-collapse {
            .tag-collapse-item {
              ::v-deep {
                .el-collapse-item__content {
                  max-height: 200px;
                  overflow-y: auto;
                }
              }
            }
          }
        }

        .tag-item {
          margin-top: 14px;
          margin-right: 14px;
          padding-right: 22px;
          max-width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          vertical-align: middle;
          position: relative;

          ::v-deep .el-tag__close {
            position: absolute;
            right: 5px;
            top: 50%;
            transform: translateY(-50%);
          }

          &.is-disabled {
            padding-right: 10px;
          }
        }
      }

      .show-all {
        margin-top: 16px;
        text-align: center;
        transition: .3s all;

        &.show {
          .expand-icon {
            transform: rotate(180deg);
          }
        }

        .expand-icon {
          display: inline-block;
          font-size: 18px;
          color: #0096cc;
          cursor: pointer;
        }
      }
    }
  }
}

.tag-selector {
  .el-select-dropdown__list {
    .el-select-dropdown__item.hover {
      background-color: #0096cc;
      color: #fff;
    }

    .el-select-dropdown__item.selected.hover {
      background-color: #0096cc;
      color: #fff;
    }
  }
}
</style>
